import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkle, MapPin, CalendarCheck, PencilSimple, Check, Umbrella, ArrowClockwise, GearSix } from '@phosphor-icons/react';
import { WeekendPrefsDialog } from '@/components/weekend/WeekendPrefsDialog';

const ATTENDEES = [
  { k: 'everyone', l: '👨‍👩‍👧‍👧 Full family' },
  { k: 'kids_mom', l: '👩‍👧‍👧 Kids + Mom' },
  { k: 'kids_dad', l: '👨‍👧‍👧 Kids + Dad' },
  { k: 'parents_only', l: '💑 Parents only' },
  { k: 'with_grandma', l: '👵 + Grandma' },
];

const BUDGETS = [
  { k: 'light', l: '₹ Light' },
  { k: 'moderate', l: '₹₹ Moderate' },
  { k: 'splurge', l: '₹₹₹ Splurge' },
];

const MEAL_LABEL = { sat_lunch: 'Sat Lunch', sat_dinner: 'Sat Dinner', sun_lunch: 'Sun Lunch', sun_dinner: 'Sun Dinner' };

function DayTimeline({ label, acts }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase text-muted-foreground mb-1.5">{label}</p>
      <div className="space-y-1.5">
        {(acts || []).map((a, i) => (
          <div key={i} className="flex gap-2 text-sm">
            <span className="text-xs text-muted-foreground font-mono w-11 shrink-0 pt-0.5">{a.time}</span>
            <span>{a.emoji}</span>
            <div className="min-w-0">
              <p className="font-medium leading-tight">{a.title}</p>
              {a.location && a.location.toLowerCase() !== 'home' && <p className="text-[11px] text-muted-foreground flex items-center gap-0.5"><MapPin size={10} /> {a.location}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan, onApprove, approving, approved }) {
  return (
    <div className={`glass-card p-5 flex flex-col gap-4 ${approved ? 'ring-2 ring-green-500/50' : ''}`} data-testid={`weekend-plan-${plan.plan_key}`}>
      <div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-lg font-bold leading-tight">{plan.title}</h3>
          {approved && <span className="text-[10px] font-bold bg-green-500/10 text-green-600 px-2 py-1 rounded-full shrink-0">✓ CHOSEN</span>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{plan.vibe}</p>
        <p className="text-xs font-semibold text-amber-600 mt-1">{plan.estimated_cost}</p>
      </div>
      <DayTimeline label="Saturday" acts={plan.sat} />
      <DayTimeline label="Sunday" acts={plan.sun} />
      {plan.meals && (
        <div className="bg-muted/40 rounded-lg p-2.5 space-y-1">
          {Object.entries(plan.meals).map(([k, v]) => v && v.mode !== 'home' && (
            <p key={k} className="text-[11px]"><b>{MEAL_LABEL[k] || k}:</b> {v.mode === 'out' ? '🍴 Out — ' : '⭐ Special — '}{v.note}</p>
          ))}
        </div>
      )}
      {plan.why && <p className="text-[11px] italic text-muted-foreground">💡 {plan.why}</p>}
      {!approved && (
        <button onClick={() => onApprove(plan.plan_key)} disabled={approving} data-testid={`approve-plan-${plan.plan_key}`}
          className="mt-auto w-full py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5">
          <Check size={15} weight="bold" /> {approving ? 'Adding to calendar…' : 'Choose this plan'}
        </button>
      )}
    </div>
  );
}

export default function WeekendPlannerPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [budget, setBudget] = useState('moderate');
  const [editingLoc, setEditingLoc] = useState(false);
  const [locInput, setLocInput] = useState('');
  const [rateNotes, setRateNotes] = useState('');
  const [showPrefs, setShowPrefs] = useState(false);
  const [attendees, setAttendees] = useState('everyone');

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/weekend/current');
      setData(r.data);
      setBudget(r.data.upcoming?.budget || r.data.settings.default_budget);
      setLocInput(r.data.settings.location);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generate = async (surprise = false) => {
    setGenerating(true);
    try {
      await api.post('/weekend/generate', { budget, attendees, surprise }, { timeout: 120000 });
      toast.success(surprise ? '🎲 Surprise plans ready — all brand-new experiences!' : 'Weekend plans ready — pick your favourite!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const approve = async (planKey) => {
    setApproving(true);
    try {
      const r = await api.post(`/weekend/${data.upcoming.weekend_id}/approve`, { plan_key: planKey });
      toast.success(`Plan approved! ${r.data.events_created} events on Calendar, ${r.data.meals_updated} meal slots updated`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setApproving(false);
  };

  const rateWeekend = async (rating) => {
    try {
      await api.post(`/weekend/${data.rating_due.weekend_id}/rate`, { rating, notes: rateNotes });
      toast.success('Thanks! The AI will learn from this 🎯');
      setRateNotes('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleAuto = async () => {
    const r = await api.put('/weekend/settings', { auto_generate: !data.settings.auto_generate });
    setData({ ...data, settings: r.data });
    toast.success(r.data.auto_generate ? 'Auto-plan every Thursday 6 PM ✓' : 'Auto-plan off — generate manually');
  };

  const saveLocation = async () => {
    const r = await api.put('/weekend/settings', { location: locInput });
    setData({ ...data, settings: r.data });
    setEditingLoc(false);
    toast.success('Location updated');
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const up = data?.upcoming;
  const weekendLabel = data ? new Date(data.weekend_start).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

  return (
    <div data-testid="weekend-planner-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Weekend Planner</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Weekend of {weekendLabel}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <MapPin size={14} />
            {editingLoc ? (
              <span className="flex items-center gap-1">
                <input value={locInput} onChange={e => setLocInput(e.target.value)} data-testid="weekend-location-input"
                  className="px-2 py-1 rounded border border-border bg-background text-xs w-48" />
                <button onClick={saveLocation} className="text-xs text-green-600 font-semibold">Save</button>
              </span>
            ) : (
              <span className="flex items-center gap-1">{data?.settings.location}
                <button onClick={() => setEditingLoc(true)} data-testid="edit-location-btn" className="text-muted-foreground hover:text-foreground"><PencilSimple size={12} /></button>
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" data-testid="auto-generate-toggle">
            <input type="checkbox" checked={!!data?.settings.auto_generate} onChange={toggleAuto} className="accent-[#FF6B35]" />
            Auto {data?.settings.auto_day ? data.settings.auto_day.charAt(0).toUpperCase() + data.settings.auto_day.slice(1) : 'Thu'} {(() => { const h = data?.settings.auto_hour ?? 18; return h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`; })()}
          </label>
          <select value={attendees} onChange={e => setAttendees(e.target.value)} data-testid="attendees-select"
            className="px-3 py-2 rounded-full border border-border bg-card text-sm font-medium">
            {ATTENDEES.map(a => <option key={a.k} value={a.k}>{a.l}</option>)}
          </select>
          <select value={budget} onChange={e => setBudget(e.target.value)} data-testid="budget-select"
            className="px-3 py-2 rounded-full border border-border bg-card text-sm font-medium">
            {BUDGETS.map(b => <option key={b.k} value={b.k}>{b.l}</option>)}
          </select>
          <button onClick={() => setShowPrefs(true)} data-testid="open-prefs-btn" title="Weekend preferences"
            className="p-2.5 rounded-full border border-border text-muted-foreground hover:text-foreground">
            <GearSix size={16} />
          </button>
          <button onClick={() => generate(true)} disabled={generating} data-testid="surprise-me-btn" title="Plan things the family has never done before"
            className="px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-amber-500 text-white text-sm font-semibold disabled:opacity-50">
            🎲 Surprise me
          </button>
          <button onClick={() => generate(false)} disabled={generating} data-testid="generate-weekend-btn"
            className="px-4 py-2 rounded-full bg-purple-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {up ? <ArrowClockwise size={15} /> : <Sparkle size={15} />}
            {generating ? 'Planning… (~20s)' : up ? 'Regenerate' : 'Plan my weekend'}
          </button>
        </div>
      </div>

      {showPrefs && data && (
        <WeekendPrefsDialog api={api} settings={data.settings}
          onClose={() => setShowPrefs(false)}
          onSaved={() => { setShowPrefs(false); fetchData(); }} />
      )}

      {up?.weather?.length > 0 && (
        <div className="flex gap-3 flex-wrap" data-testid="weekend-weather">
          {up.weather.map(w => (
            <div key={w.date} className="glass-card px-4 py-2.5 flex items-center gap-3">
              <span className="text-2xl">{w.emoji}</span>
              <div>
                <p className="text-xs font-semibold">{new Date(w.date).toLocaleDateString(undefined, { weekday: 'short' })} — {w.label}</p>
                <p className="text-[11px] text-muted-foreground">{w.temp_min}–{w.temp_max}°C · rain {w.rain_chance}%</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.rating_due && (
        <div className="glass-card p-5 border-amber-500/40" data-testid="weekend-rating-card">
          <h3 className="font-semibold text-sm mb-1">How was last weekend? <span className="text-muted-foreground font-normal">— "{data.rating_due.chosen?.title}"</span></h3>
          <p className="text-xs text-muted-foreground mb-3">Your feedback teaches the AI what your family loves.</p>
          <input value={rateNotes} onChange={e => setRateNotes(e.target.value)} placeholder="Optional: what worked / didn't? (e.g. kids loved the beach, museum was crowded)"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm mb-3" data-testid="rating-notes-input" />
          <div className="flex gap-2">
            <button onClick={() => rateWeekend('loved')} data-testid="rate-loved-btn" className="px-4 py-2 rounded-full bg-green-600 text-white text-sm font-semibold">😍 Loved it</button>
            <button onClick={() => rateWeekend('good')} data-testid="rate-good-btn" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold">🙂 Good</button>
            <button onClick={() => rateWeekend('meh')} data-testid="rate-meh-btn" className="px-4 py-2 rounded-full border border-border text-sm font-semibold">😐 Meh</button>
          </div>
        </div>
      )}

      {!up && !generating && (
        <div className="glass-card p-12 text-center">
          <Umbrella size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-1">No plan for this weekend yet</h3>
          <p className="text-sm text-muted-foreground mb-4">The AI will consider the girls' interests, your calendar, Mumbai weather and your budget — then give you 3 plans to pick from.</p>
          <button onClick={() => generate(false)} data-testid="generate-first-btn" className="px-5 py-2.5 rounded-full bg-purple-600 text-white text-sm font-semibold"><Sparkle size={15} className="inline mr-1" /> Plan my weekend</button>
        </div>
      )}

      {up?.status === 'approved' && up.chosen && (
        <div className="glass-card p-5 border-green-500/40" data-testid="approved-weekend">
          <div className="flex items-center gap-2 mb-3">
            <CalendarCheck size={18} className="text-green-600" />
            <h3 className="font-semibold">This weekend: {up.chosen.title} <span className="text-xs text-muted-foreground font-normal">— on your Calendar ✓ & in Kid Mode 🧒</span></h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <DayTimeline label="Saturday" acts={up.chosen.sat} />
            <DayTimeline label="Sunday" acts={up.chosen.sun} />
          </div>
        </div>
      )}

      {up?.status === 'draft' && (
        <>
          {up.surprise && (
            <div className="flex items-center gap-2 text-sm font-semibold text-pink-500" data-testid="surprise-badge">
              🎲 Surprise Weekend — everything below is something your family has never done before!
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="weekend-options">
            {up.options.map(p => (
              <PlanCard key={p.plan_key} plan={p} onApprove={approve} approving={approving} approved={false} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
